import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  MapPin,
  Star,
  ArrowLeft,
  Loader2,
  DollarSign,
  ArrowLeftRight,
  Store,
  MessageSquare,
  Share2,
  Leaf,
  Wrench,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ItemDetail = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [hoveredRating, setHoveredRating] = useState(0);

  const isOwner = user?.id === item?.seller_id;

  useEffect(() => {
    fetchItem();
  }, [itemId]);

  const fetchItem = async () => {
    try {
      const [itemRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/marketplace/${itemId}`),
        axios.get(`${API}/marketplace/${itemId}/reviews`)
      ]);
      setItem(itemRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Error fetching item:', error);
      toast.error('Item not found');
      navigate('/marketplace');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/marketplace/${itemId}/reviews`, newReview);
      setReviews([response.data, ...reviews]);
      
      // Update item rating
      const newRatingCount = item.rating_count + 1;
      const newAvgRating = ((item.average_rating * item.rating_count) + newReview.rating) / newRatingCount;
      setItem({ ...item, average_rating: newAvgRating, rating_count: newRatingCount });
      
      setNewReview({ rating: 5, comment: '' });
      setReviewOpen(false);
      toast.success('Review submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating, interactive = false, size = 'w-5 h-5') => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={!interactive}
            onClick={() => interactive && setNewReview({ ...newReview, rating: star })}
            onMouseEnter={() => interactive && setHoveredRating(star)}
            onMouseLeave={() => interactive && setHoveredRating(0)}
            className={interactive ? 'cursor-pointer' : 'cursor-default'}
          >
            <Star
              className={`${size} transition-colors ${
                star <= (interactive ? (hoveredRating || newReview.rating) : rating)
                  ? 'text-warning fill-warning'
                  : 'text-muted-foreground/30'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  const nextImage = () => {
    if (item?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % item.images.length);
    }
  };

  const prevImage = () => {
    if (item?.images?.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + item.images.length) % item.images.length);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!item) return null;

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div className="aspect-square bg-muted rounded-2xl overflow-hidden relative">
              {item.images && item.images.length > 0 ? (
                <>
                  <img 
                    src={item.images[currentImageIndex]}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                  {item.images.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background/80 flex items-center justify-center hover:bg-background transition-colors"
                      >
                        <ChevronLeft className="w-6 h-6" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background/80 flex items-center justify-center hover:bg-background transition-colors"
                      >
                        <ChevronRight className="w-6 h-6" />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  {item.item_type === 'service' ? (
                    <Wrench className="w-24 h-24 text-muted-foreground/30" />
                  ) : (
                    <Leaf className="w-24 h-24 text-muted-foreground/30" />
                  )}
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {item.images && item.images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {item.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-20 h-20 rounded-lg overflow-hidden shrink-0 border-2 transition-colors ${
                      idx === currentImageIndex ? 'border-primary' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Item Details */}
          <div className="space-y-6">
            <div>
              <div className="flex flex-wrap gap-2 mb-3">
                <Badge className="bg-primary/10 text-primary">{item.category}</Badge>
                {item.item_type === 'service' && (
                  <Badge className="bg-secondary/10 text-secondary">Service</Badge>
                )}
                {item.is_trade && (
                  <Badge variant="outline">
                    <ArrowLeftRight className="w-3 h-3 mr-1" />
                    Open to Trade
                  </Badge>
                )}
                <Badge variant="secondary">{item.condition}</Badge>
              </div>

              <h1 className="text-3xl font-heading font-medium mb-2">{item.title}</h1>
              
              {/* Rating */}
              <div className="flex items-center gap-2 mb-4">
                {renderStars(item.average_rating)}
                <span className="font-medium">{item.average_rating.toFixed(1)}</span>
                <span className="text-muted-foreground">({item.rating_count} reviews)</span>
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-4xl font-heading font-medium text-secondary flex items-center">
                  <DollarSign className="w-8 h-8" />
                  {item.price.toFixed(2)}
                </span>
              </div>

              <p className="text-muted-foreground mb-6">{item.description}</p>

              {/* Location */}
              <div className="flex items-center gap-2 text-muted-foreground mb-6">
                <MapPin className="w-4 h-4" />
                <span>{item.location}</span>
              </div>

              {/* Seller Info */}
              <Card className="mb-6">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar 
                        className="w-12 h-12 cursor-pointer"
                        onClick={() => navigate(`/profile/${item.seller_username}`)}
                      >
                        <AvatarFallback>{item.seller_username?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">@{item.seller_username}</p>
                        <p className="text-sm text-muted-foreground">
                          Listed {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                    {item.shop_id && (
                      <Button variant="outline" size="sm" onClick={() => navigate(`/shop/${item.shop_id}`)}>
                        <Store className="w-4 h-4 mr-2" />
                        View Shop
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex gap-3">
                {!isOwner && (
                  <>
                    <Button 
                      className="flex-1 btn-primary"
                      onClick={() => navigate(`/messages?to=${item.seller_id}`)}
                      data-testid="contact-seller-btn"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Contact Seller
                    </Button>
                    <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" data-testid="review-item-btn">
                          <Star className="w-4 h-4 mr-2" />
                          Review
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="font-heading">Review this Item</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Rating</label>
                            <div className="flex justify-center py-2">
                              {renderStars(newReview.rating, true, 'w-8 h-8')}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Your Review (optional)</label>
                            <Textarea
                              placeholder="Share your experience..."
                              value={newReview.comment}
                              onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                              rows={4}
                              data-testid="item-review-comment"
                            />
                          </div>
                          <Button 
                            className="w-full btn-primary"
                            onClick={handleSubmitReview}
                            disabled={submitting}
                            data-testid="submit-item-review-btn"
                          >
                            {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            Submit Review
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </>
                )}
                <Button 
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success('Link copied!');
                  }}
                >
                  <Share2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-12">
          <h2 className="text-2xl font-heading font-medium mb-6">Reviews ({reviews.length})</h2>
          
          {reviews.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <Star className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground mb-4">No reviews yet</p>
                {!isOwner && (
                  <Button className="btn-primary" onClick={() => setReviewOpen(true)}>
                    Be the First to Review
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {reviews.map((review) => (
                <Card key={review.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Avatar 
                        className="w-10 h-10 cursor-pointer"
                        onClick={() => navigate(`/profile/${review.reviewer_username}`)}
                      >
                        <AvatarImage src={review.reviewer_avatar} />
                        <AvatarFallback>{review.reviewer_username?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span 
                            className="font-medium cursor-pointer hover:underline"
                            onClick={() => navigate(`/profile/${review.reviewer_username}`)}
                          >
                            @{review.reviewer_username}
                          </span>
                          <span className="text-sm text-muted-foreground">
                            {formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}
                          </span>
                        </div>
                        <div className="mb-2">
                          {renderStars(review.rating, false, 'w-4 h-4')}
                        </div>
                        {review.comment && (
                          <p className="text-muted-foreground">{review.comment}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemDetail;
